export enum StaffGender {
  MALE = 'male',
  FEMALE = 'female',
  OTHER = 'other',
}

export enum EmploymentType {
  PERMANENT = 'permanent',
  CONTRACTUAL = 'contractual',
  PART_TIME = 'part_time',
}

export enum StaffDepartment {
  CLINIC_OPERATIONS = 'clinic_operations',
  PHARMACY = 'pharmacy',
  LAB_COLLECTION = 'lab_collection',
  XRAY_ECG = 'xray_ecg',
  DELIVERY = 'delivery',
  MARKETING = 'marketing',
  ACCOUNTS = 'accounts',
  HR_ADMIN = 'hr_admin',
}

export enum EmployeeStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  RESIGNED = 'resigned',
  TERMINATED = 'terminated',
}

export enum SalaryPaymentMode {
  BANK_TRANSFER = 'bank_transfer',
  UPI = 'upi',
  CASH = 'cash',
  CHEQUE = 'cheque',
}

export enum StaffDocumentType {
  AADHAAR = 'aadhaar',
  PAN = 'pan',
  DRIVING_LICENSE = 'driving_license',
  VOTER_ID = 'voter_id',
  PHARMACY_REGISTRATION = 'pharmacy_registration',
  LAB_TECHNICAL_QUALIFICATION = 'lab_technical_qualification',
  ADDRESS_PROOF = 'address_proof',
  EDUCATIONAL_CERTIFICATE = 'educational_certificate',
  EXPERIENCE_LETTER = 'experience_letter',
  OTHER = 'other',
}

// User-friendly label mappings
export const StaffGenderLabels: Record<StaffGender, string> = {
  [StaffGender.MALE]: 'Male',
  [StaffGender.FEMALE]: 'Female',
  [StaffGender.OTHER]: 'Other',
};

export const EmploymentTypeLabels: Record<EmploymentType, string> = {
  [EmploymentType.PERMANENT]: 'Permanent',
  [EmploymentType.CONTRACTUAL]: 'Contractual',
  [EmploymentType.PART_TIME]: 'Part-Time',
};

export const StaffDepartmentLabels: Record<StaffDepartment, string> = {
  [StaffDepartment.CLINIC_OPERATIONS]: 'Clinic Operations',
  [StaffDepartment.PHARMACY]: 'Pharmacy',
  [StaffDepartment.LAB_COLLECTION]: 'Lab Collection',
  [StaffDepartment.XRAY_ECG]: 'X-Ray & ECG',
  [StaffDepartment.DELIVERY]: 'Delivery',
  [StaffDepartment.MARKETING]: 'Marketing',
  [StaffDepartment.ACCOUNTS]: 'Accounts',
  [StaffDepartment.HR_ADMIN]: 'HR & Admin',
};

export const EmployeeStatusLabels: Record<EmployeeStatus, string> = {
  [EmployeeStatus.ACTIVE]: 'Active',
  [EmployeeStatus.INACTIVE]: 'Inactive',
  [EmployeeStatus.RESIGNED]: 'Resigned',
  [EmployeeStatus.TERMINATED]: 'Terminated',
};

export const SalaryPaymentModeLabels: Record<SalaryPaymentMode, string> = {
  [SalaryPaymentMode.BANK_TRANSFER]: 'Bank Transfer',
  [SalaryPaymentMode.UPI]: 'UPI',
  [SalaryPaymentMode.CASH]: 'Cash',
  [SalaryPaymentMode.CHEQUE]: 'Cheque',
};

export const StaffDocumentTypeLabels: Record<StaffDocumentType, string> = {
  [StaffDocumentType.AADHAAR]: 'Aadhaar Card',
  [StaffDocumentType.PAN]: 'PAN Card',
  [StaffDocumentType.DRIVING_LICENSE]: 'Driving License',
  [StaffDocumentType.VOTER_ID]: 'Voter ID',
  [StaffDocumentType.PHARMACY_REGISTRATION]: 'Pharmacy Registration',
  [StaffDocumentType.LAB_TECHNICAL_QUALIFICATION]: 'Lab Technical Qualification',
  [StaffDocumentType.ADDRESS_PROOF]: 'Address Proof',
  [StaffDocumentType.EDUCATIONAL_CERTIFICATE]: 'Educational Certificate',
  [StaffDocumentType.EXPERIENCE_LETTER]: 'Experience Letter',
  [StaffDocumentType.OTHER]: 'Other Document',
};

// Data Interfaces
export interface StaffSignupPayload {
  role: string;
  clinicId?: string;
  username: string;
  name: string;
  password?: string;
  mobileNumber: string;
  gender: StaffGender | string;
  department: StaffDepartment | string;
  designation: string;
}

export interface StaffCompleteDetailsPayload {
  fullName?: string;
  fatherSpouseName?: string;
  dateOfBirth?: string;
  gender?: StaffGender | string;
  alternateMobileNumber?: string;
  personalEmail?: string;
  currentAddress?: string;
  currentPincode?: string;
  permanentAddress?: string;
  permanentPincode?: string;
  emergencyContactName?: string;
  emergencyContactNumber?: string;
  emergencyContactRelation?: string;
  bloodGroup?: string;
  joiningDate?: string;
  employmentType?: EmploymentType | string;
  department?: StaffDepartment | string;
  designation?: string;
  reportingManagerId?: string;
  employeeStatus?: EmployeeStatus | string;
  monthlySalary?: number | string;
  bankAccountHolderName?: string;
  bankName?: string;
  accountNumber?: string;
  ifscCode?: string;
  upiId?: string;
  uanPfNumber?: string;
  esicNumber?: string;
  panNumber?: string;
  salaryPaymentMode?: SalaryPaymentMode | string;
  aadhaarNumber?: string;
  drivingLicence?: string;
  voterId?: string;
  pharmacyRegistrationNumber?: string;
}

export interface StaffDocument {
  id: string;
  staffId: string;
  documentType: StaffDocumentType | string;
  documentPath: string;
  documentUrl: string | null;
  documentName: string | null;
  uploadedAt: string;
}

export interface SalaryRevisionItem {
  id: string;
  staffId: string;
  previousSalary: number;
  revisedSalary: number;
  effectiveDate: string;
  remarks?: string;
  createdAt: string;
}

export interface SalaryRevisionPayload {
  staffId: string;
  previousSalary: number;
  revisedSalary: number;
  effectiveDate: string;
  remarks?: string;
}

export interface StaffListItem {
  id: string;
  clinicId: string;
  employeeCode?: string;
  fullName: string;
  profilePhoto?: string | null;
  mobileNumber: string;
  department: StaffDepartment | string;
  designation: string;
  employmentType: EmploymentType | string;
  employeeStatus: EmployeeStatus | string;
  joiningDate?: string;
  createdAt?: string;
}

export interface StaffDetails extends StaffListItem {
  fatherSpouseName?: string;
  dateOfBirth?: string;
  gender?: StaffGender | string;
  alternateMobileNumber?: string;
  personalEmail?: string;
  currentAddress?: string;
  currentPincode?: string;
  permanentAddress?: string;
  permanentPincode?: string;
  emergencyContactName?: string;
  emergencyContactNumber?: string;
  emergencyContactRelation?: string;
  bloodGroup?: string;
  reportingManagerId?: string;
  reportingManagerName?: string;
  monthlySalary?: number;
  bankAccountHolderName?: string;
  bankName?: string;
  accountNumber?: string;
  ifscCode?: string;
  upiId?: string;
  uanPfNumber?: string;
  esicNumber?: string;
  panNumber?: string;
  salaryPaymentMode?: SalaryPaymentMode | string;
  aadhaarNumber?: string;
  drivingLicence?: string;
  voterId?: string;
  pharmacyRegistrationNumber?: string;
  documents?: StaffDocument[];
  salaryRevisions?: SalaryRevisionItem[];
}

export interface StaffOption {
  id: string;
  name: string;
  profilePhoto?: string | null;
  mobileNumber: string;
  employeeCode?: string;
  designation: string;
  department: string;
}

export interface StaffFilters {
  searchText?: string;
  department?: string;
  employeeStatus?: string;
  employmentType?: string;
  designation?: string;
  clinicId?: string;
}

export interface StaffListingParams {
  page?: number;
  pageSize?: number;
  clinicId?: string;
  department?: string;
  designation?: string;
  employeeStatus?: string;
  employmentType?: string;
  searchText?: string;
}

export interface StaffPaginationData {
  currentPage: number;
  itemsPerPage: number;
  totalItemsCount: number;
  totalPages: number;
  nextPage: number | null;
  previousPage: number | null;
}
